import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { IAlert } from '../../../types/Alert';
import { IManualOrder } from '../../../types/ManualOrder';
import { IOrder } from '../../../types/Order';
import {
  IEscrowMember,
  ISettlementTeam,
  ISigner,
} from '../../../types/Parties';
import { IUpdateSigner } from '../../../types/UpdateSigner';
import { AppThunk } from '../../redux-store/store';
import { getModel } from '../../utils/manualOrderModel';
import { selectCurrentUser, selectOffices } from '../auth/authSlice';
import { selectParsedFNM } from '../fmnParse/fmnParseSlice';
import { selectConfig } from '../global/globalSlice';
import { RootState } from '../store';
import { beginFileDownload } from './ordersFileDownloadAPI';
import {
  beginUploadDocuments,
  uploadFileToS3,
  uploadFileToSolex,
} from './ordersFileUploadAPI';
import {
  DownloadFileIProps,
  ViewOrderIProps,
  ViewOrdersIProps,
} from './ordersTypes';
import {
  addSettlementAgentTeamMembers,
  cancelOrder,
  changeNotary,
  createFAOrder,
  fetchNotaries,
  fetchSettlementAgent,
  reloadFastData,
  reloadOrder,
  saveOrder,
  submitOrder,
  unlockParty,
  unlockSession,
  updateOrderActions,
  updateSignerInformation,
  viewOrder,
  viewOrders,
} from './viewOrdersAPI';
import { UnlockSessionRequest } from './viewOrdersAPI.types';

export interface IOrdersState {
  order?: IOrder;
  reloading?: boolean;
  orders?: IOrder[];
  isLoading?: boolean;
  isFetching?: boolean;
  isFetchingText?: string | null;
  isFetchingOrder?: boolean;
  alertUser?: IAlert | null;
  errors?: string[];
  VersionNumber?: number | null;
  paginationToken?: string | null;
  notaries?: Array<any> | null;
  loadingRONNotaries?: boolean;
  isReassigningNotary?: boolean;
  isAddingCoSettlement?: boolean;
  isIdentifingWitness?: boolean;
}

const initialState: IOrdersState = {
  order: undefined,
  reloading: false,
  orders: [],
  isLoading: false,
  isFetching: false,
  isFetchingText: null,
  isFetchingOrder: false,
  alertUser: null,
  errors: undefined,
  VersionNumber: null,
  paginationToken: null,
  notaries: null,
  loadingRONNotaries: false,
  isReassigningNotary: false,
  isIdentifingWitness: false,
};

export const fetchNotariesAsync = createAsyncThunk(
  'orders/fetchNotaries',
  async (data: any, { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    try {
      const response = await fetchNotaries(data, config);

      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);
//order-management/change-notary
export const saveOrderAsync = createAsyncThunk(
  'orders/saveOrder',
  async (order: any, { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    try {
      const response = await saveOrder(order, config);
      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const changeNotaryAsync = createAsyncThunk(
  'orders/changeNotary',
  async (
    data: {
      OrderId?: string;
      NotaryEmail?: string;
    },
    { getState, rejectWithValue }
  ) => {
    const config: any = selectConfig(getState() as any);
    try {
      const response = await changeNotary(data, config);
      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const updateOrderActionsAsync = createAsyncThunk(
  'orders/updateOrderActions',
  async (
    data: {
      OrderId: string;
      ChecklistId: string;
      Complete: boolean;
      Signer?: string;
    },
    { getState, rejectWithValue }
  ) => {
    const config: any = selectConfig(getState() as any);
    try {
      const response = await updateOrderActions(data, config);
      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const submitOrderAsync = createAsyncThunk(
  'orders/submitOrder',
  async (_, { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    const order: any = selectOrder(getState() as any);
    try {
      const response = await submitOrder(order, config);
      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const createFAOrderAsync = createAsyncThunk(
  'orders/createFAOrder',
  async (values: any, { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    const order: any = selectOrder(getState() as any);
    try {
      const response = await createFAOrder(values, order, config);
      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const reloadFastDataAsync = createAsyncThunk(
  'orders/reloadFastData',
  async (values: any, { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    const order: any = selectOrder(getState() as any);
    try {
      const response = await reloadFastData(values, order, config);
      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const createThirdPartyOrderAsync = createAsyncThunk(
  'orders/createThirdPartyOrder',
  async (values: IManualOrder, { getState, rejectWithValue }) => {
    const { loanNumber, orderType } = values;
    const parsedFNM: any = selectParsedFNM(getState() as any);
    const userOffices: any = selectOffices(getState() as any);
    const currentUser: any = selectCurrentUser(getState() as any);

    try {
      const skeleton: any = getModel(
        userOffices,
        loanNumber,
        orderType,
        parsedFNM,
        currentUser
      );
      return skeleton;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const beginFileDownloadAsync = createAsyncThunk(
  'orders/beginFileDownload',
  async (data: DownloadFileIProps, { getState, rejectWithValue }) => {
    const { path, requestParams } = data;
    const config: any = selectConfig(getState() as any);
    try {
      const response = await beginFileDownload(path, requestParams, config);
      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const viewOrdersAsync = createAsyncThunk(
  'orders/viewOrders',
  async (data: ViewOrdersIProps, { getState, rejectWithValue, signal }) => {
    const { query, paginationDirection } = data;
    const splitQuery = query.split('&');

    // This part gate keeps any empty/all branches office ids
    const isAllBranches = splitQuery.includes('OfficeIds=allbranches');
    const isEmptyBranches = splitQuery.includes('OfficeIds=');
    let formattedQuery = isAllBranches
      ? splitQuery.filter((x) => x !== 'OfficeIds=allbranches')
      : splitQuery;
    formattedQuery = isEmptyBranches
      ? splitQuery.filter((x) => x !== 'OfficeIds=')
      : formattedQuery;

    const config: any = selectConfig(getState() as any);
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });

    try {
      const response = await viewOrders(
        formattedQuery.join('&'),
        paginationDirection,
        config,
        source.token
      );
      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const viewOrderAsync = createAsyncThunk(
  'orders/viewOrder',
  async (data: ViewOrderIProps, { getState, rejectWithValue }) => {
    const { orderId } = data;
    const config: any = selectConfig(getState() as any);
    try {
      const response = await viewOrder(orderId, config);
      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const reloadOrderAsync = createAsyncThunk(
  'orders/reloadOrder',
  async (data: ViewOrderIProps, { getState, rejectWithValue }) => {
    const { orderId } = data;
    const config: any = selectConfig(getState() as any);
    const order: any = selectOrder(getState() as any);

    try {
      const response = await reloadOrder(orderId, config, order);
      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const cancelOrderAsync = createAsyncThunk(
  'orders/cancelOrder',
  async (data: ViewOrderIProps, { getState, rejectWithValue }) => {
    const { orderId } = data;
    const config: any = selectConfig(getState() as any);
    try {
      const response = await cancelOrder(orderId, config);
      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const unlockPartyAsync = createAsyncThunk(
  'orders/unlockParty',
  async (object: Record<string, unknown>, { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    try {
      const response = await unlockParty(object, config);
      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const unlockSessionAsync = createAsyncThunk(
  'unlockSession',
  async (
    requestPayload: UnlockSessionRequest,
    { getState, rejectWithValue }
  ) => {
    const config = selectConfig(getState() as any);
    try {
      return await unlockSession(requestPayload, config);
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const beginUploadDocumentsAsync =
  (data: any): AppThunk =>
  (dispatch, getState) => {
    (async function () {
      const { id, documentNamesArray, documentDataArray } = data;
      const config: any = selectConfig(getState() as any);
      const order = selectOrder(getState() as any);
      const documentCount = order?.OrderDetails?.Documents?.length || 0;
      const res: any = await beginUploadDocuments(
        id,
        documentNamesArray,
        documentDataArray,
        config
      );
      const { signedUrls, documents, md5Array } = res;

      for (let i = 0; i < documents.length; i++) {
        const doc = documents[i];
        const md5 = md5Array[i];
        const upload: any = await uploadFileToS3(
          signedUrls[doc.Name],
          doc.File,
          md5
        );
        if (upload?.status === 200) {
          dispatch(uploadDocumentSuccess(documentCount - documents.length + i));
        } else {
          dispatch(uploadDocumentError(documentCount - documents.length + i));
        }
      }
    })();
  };

export const UploadDocumentsOrderAsync = createAsyncThunk(
  'uploadInkSignDocument',
  async (
    data: Record<string, any>,
    { getState, dispatch, rejectWithValue }
  ) => {
    const {
      id,
      documentNamesArray,
      documentDataArray,
      solexSessionId,
      signingType,
    } = data;
    const config: any = selectConfig(getState() as any);
    const order = selectOrder(getState() as any);
    // Send the documents to the receive the pre-sign url to the future provision bucket
    const res: any = await beginUploadDocuments(
      id,
      documentNamesArray,
      documentDataArray,
      config
    );
    const { signedUrls, documents, md5Array } = res;
    const documentCount = order?.OrderDetails?.Documents?.length || 0;
    for (let i = 0; i < documents.length; i++) {
      const doc = documents[i];
      const md5 = md5Array[i];
      // Upload the documents to the S3 bucket
      try {
        const upload: any = await uploadFileToS3(
          signedUrls[doc.Name],
          doc.File,
          md5
        );
        if (upload?.status === 200) {
          const documentToUpload: any = {
            ImageSize: doc.ImageSize,
            ImageType: doc.ImageType,
            Location: doc.Location,
            Name: doc.Name,
            IsFullyExecutedInkSign: true,
          };
          if (signingType) {
            documentToUpload.SigningType = signingType;
          }
          await uploadFileToSolex(id, solexSessionId, documentToUpload, config);
        }
      } catch (err) {
        dispatch(uploadDocumentError(documentCount - documents.length + i));
        return rejectWithValue(err);
      }
    }
  }
);

export const updateSignerInformationAsync = createAsyncThunk(
  'orders/updateSignerInformation',
  async (
    data: {
      OrderId: string;
      SignerInformation: IUpdateSigner;
    },
    { getState, rejectWithValue }
  ) => {
    const config: any = selectConfig(getState() as any);
    try {
      const response = await updateSignerInformation(data, config);
      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const addCoSettlementAgentsToOrderAsync = createAsyncThunk(
  'admin/add-settlement-agents',
  async (
    data: {
      OrderId: string;
      TeamMembersListToAdd: ISettlementTeam[];
    },
    { getState, rejectWithValue }
  ) => {
    const config: any = selectConfig(getState() as any);
    try {
      const response = await addSettlementAgentTeamMembers(data, config);
      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const fetchSettlementAgentAsync = createAsyncThunk(
  'orders/fetchSettlementAgent',
  async (data: any, { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    try {
      const response = await fetchSettlementAgent(data, config);
      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setAlert: (state: IOrdersState, action: PayloadAction<any>) => {
      state.isLoading = false;
      state.alertUser = action.payload
        ? action.payload.type
          ? { type: action.payload.type, text: action.payload.text }
          : { type: 'info', text: action.payload }
        : null;
    },
    updateCreateOrderObject: (
      state: IOrdersState,
      action: PayloadAction<IOrder>
    ) => {
      state.isLoading = false;
      state.order = action.payload;
      state.alertUser = null;
      state.errors = undefined;
    },
    updateSelectedParty: (state: IOrdersState, action: PayloadAction<any>) => {
      state.isLoading = false;
      state.alertUser = null;
      state.errors = undefined;
      if (action.payload.type === 'signers') {
        const updatedOrderObj = Object.assign({}, state.order, {
          OrderDetails: {
            ...state.order?.OrderDetails,
            Signers: action.payload.selected,
          },
        });
        state.order = updatedOrderObj;
      }
      if (action.payload.type === 'team') {
        const updatedOrderObj = Object.assign({}, state.order, {
          OrderDetails: {
            ...state.order?.OrderDetails,
            EscrowTeam: action.payload.selected,
          },
        });
        state.order = updatedOrderObj;
      }
      if (action.payload.type === 'settlement') {
        const updatedOrderObj = Object.assign({}, state.order, {
          OrderDetails: {
            ...state.order?.OrderDetails,
            SettlementTeam: action.payload.selected,
          },
        });
        state.order = updatedOrderObj;
      }
    },
    updateSignerArray: (
      state: IOrdersState,
      action: PayloadAction<ISigner[] | ISettlementTeam[] | IEscrowMember[]>
    ) => {
      const updatedOrderObj = Object.assign({}, state.order, {
        OrderOverview: {
          ...state.order?.OrderOverview,
          Signers: action.payload,
        },
      });
      state.order = updatedOrderObj;
      state.isLoading = false;
      state.alertUser = null;
      state.errors = undefined;
    },
    updateOrderActions: (
      state: IOrdersState,
      action: PayloadAction<ISigner[] | ISettlementTeam[] | IEscrowMember[]>
    ) => {
      const updatedOrderObj = Object.assign({}, state.order, {
        OrderOverview: {
          ...state.order?.OrderOverview,
          Signers: action.payload,
        },
      });
      state.order = updatedOrderObj;
      state.isLoading = false;
      state.alertUser = null;
      state.errors = undefined;
    },
    updateOrderDetailsSignerArray: (
      state: IOrdersState,
      action: PayloadAction<ISigner[]>
    ) => {
      const updatedOrderObj = Object.assign({}, state.order, {
        OrderDetails: { ...state.order?.OrderDetails, Signers: action.payload },
      });
      state.order = updatedOrderObj;
      state.isLoading = false;
      state.alertUser = null;
      state.errors = undefined;
    },
    updateEscrowTeamArray: (
      state: IOrdersState,
      action: PayloadAction<ISigner[] | ISettlementTeam[] | IEscrowMember[]>
    ) => {
      const updatedOrderObj = Object.assign({}, state.order, {
        OrderOverview: {
          ...state.order?.OrderOverview,
          EscrowTeam: action.payload,
        },
      });
      state.order = updatedOrderObj;
      state.isLoading = false;
      state.alertUser = null;
      state.errors = undefined;
    },
    updateSettlementTeamArray: (
      state: IOrdersState,
      action: PayloadAction<ISigner[] | ISettlementTeam[] | IEscrowMember[]>
    ) => {
      const updatedOrderObj = Object.assign({}, state.order, {
        OrderOverview: {
          ...state.order?.OrderOverview,
          SettlementTeam: action.payload,
        },
      });
      state.order = updatedOrderObj;
      state.isLoading = false;
      state.alertUser = null;
      state.errors = undefined;
    },
    uploadDocumentSuccess: (
      state: IOrdersState,
      action: PayloadAction<number>
    ) => {
      const updatedOrderObj = Object.assign({}, state.order, {
        OrderOverview: { ...state.order?.OrderOverview },
      });

      if (
        updatedOrderObj?.OrderDetails?.Documents &&
        updatedOrderObj?.OrderDetails?.Documents[action.payload]
      ) {
        updatedOrderObj.OrderDetails.Documents[action.payload].uploading =
          false;
        updatedOrderObj.OrderDetails.Documents[action.payload].errorUploading =
          false;
      }

      state.order = updatedOrderObj;
      state.isLoading = false;
      state.errors = undefined;
    },
    uploadDocumentError: (
      state: IOrdersState,
      action: PayloadAction<number>
    ) => {
      const updatedOrderObj = Object.assign({}, state.order, {
        OrderOverview: { ...state.order?.OrderOverview },
      });

      if (
        updatedOrderObj?.OrderDetails?.Documents &&
        updatedOrderObj?.OrderDetails?.Documents[action.payload]
      ) {
        updatedOrderObj.OrderDetails.Documents[action.payload].uploading =
          false;
        updatedOrderObj.OrderDetails.Documents[action.payload].errorUploading =
          true;
      }

      state.order = updatedOrderObj;
      state.isLoading = false;
      state.errors = undefined;
    },
    resetCreateFile: (state: IOrdersState) => {
      state.isLoading = false;
      state.order = undefined;
      state.errors = undefined;
      state.alertUser = null;
    },
    setIsLoading: (state: IOrdersState, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // DOWNLOAD FILES
      .addCase(beginFileDownloadAsync.pending, (state) => {
        state.isLoading = true;
        state.alertUser = null;
      })
      .addCase(beginFileDownloadAsync.fulfilled, (state: any) => {
        state.isLoading = false;
        state.alertUser = null;
      })
      .addCase(beginFileDownloadAsync.rejected, (state: any, action: any) => {
        state.isLoading = true;
        state.alertUser = { type: 'error', text: action.payload };
      })
      // CREATE THIRD PARTY ORDER
      .addCase(createThirdPartyOrderAsync.pending, (state) => {
        state.isLoading = false;
        state.isFetchingOrder = true;
        state.alertUser = null;
        state.errors = undefined;
      })
      .addCase(
        createThirdPartyOrderAsync.fulfilled,
        (state: any, action: any) => {
          state.order = action.payload;
          state.orders = [];
          state.isLoading = false;
          state.isFetchingOrder = false;
          state.alertUser = null;
          state.errors = undefined;
        }
      )
      .addCase(
        createThirdPartyOrderAsync.rejected,
        (state: any, action: any) => {
          state.alertUser = null;
          state.order = undefined;
          state.errors = action.payload;
          state.isLoading = false;
          state.isFetchingOrder = false;
        }
      )
      // CREATE FA ORDER
      .addCase(createFAOrderAsync.pending, (state) => {
        state.isLoading = false;
        state.isFetchingOrder = true;
        state.alertUser = null;
        state.errors = undefined;
      })
      .addCase(createFAOrderAsync.fulfilled, (state: any, action: any) => {
        const orderSkeleton = {
          ...action.payload,
          OrderOverview: action.payload?.OrderDetails,
          OrderDetails: {
            SigningDate: undefined,
            RON: action.payload?.OrderDetails?.RON,
            Signers: [],
            EscrowTeam: [],
            Documents: [],
          },
        };

        state.order = orderSkeleton;
        state.orders = [];
        state.isLoading = false;
        state.isFetchingOrder = false;
        state.alertUser = null;
        state.errors = undefined;
      })
      .addCase(createFAOrderAsync.rejected, (state: any, action: any) => {
        state.alertUser = null;
        state.order = undefined;
        state.errors = action.payload;
        state.isLoading = false;
        state.isFetchingOrder = false;
      })
      // RELOAD FAST DATA
      .addCase(reloadFastDataAsync.pending, (state) => {
        state.isLoading = false;
        state.isFetchingOrder = true;
        state.alertUser = null;
        state.errors = undefined;
      })
      .addCase(reloadFastDataAsync.fulfilled, (state: any, action: any) => {
        state.order = action.payload;
        state.isLoading = false;
        state.isFetchingOrder = false;
        state.alertUser = null;
        state.errors = undefined;
      })
      .addCase(reloadFastDataAsync.rejected, (state: any, action: any) => {
        state.alertUser = { text: action.payload, type: 'error' };
        state.errors = undefined;
        state.isLoading = false;
        state.isFetchingOrder = false;
      })
      // VIEW ORDERS
      .addCase(viewOrdersAsync.pending, (state) => {
        state.isLoading = true;
        state.notaries = null;
        state.alertUser = null;
      })
      .addCase(viewOrdersAsync.fulfilled, (state: any, action: any) => {
        state.order = undefined;
        state.orders = action.payload.Orders;
        state.isLoading = false;
        state.paginationToken = action.payload.PaginationToken;
      })
      .addCase(viewOrdersAsync.rejected, (state: any, action: any) => {
        if (action.payload) {
          state.alertUser = { type: 'error', text: action.payload };
          state.isLoading = false;
        }
        state.order = undefined;
      })
      // VIEW ORDER
      .addCase(viewOrderAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(viewOrderAsync.fulfilled, (state: any, action: any) => {
        state.order = action.payload;
        state.orders = [];
        state.isLoading = false;
        state.paginationToken = null;
      })
      .addCase(viewOrderAsync.rejected, (state: any) => {
        state.order = null;
        state.orders = [];
        state.isLoading = false;
        state.paginationToken = null;
      })
      // RELOAD ORDER
      .addCase(reloadOrderAsync.pending, (state) => {
        state.isLoading = false;
        state.reloading = true;
      })
      .addCase(reloadOrderAsync.fulfilled, (state: any, action: any) => {
        state.order = action.payload.order;
        state.orders = [];
        state.isLoading = false;
        state.reloading = false;
        state.paginationToken = null;
      })
      .addCase(reloadOrderAsync.rejected, (state: any) => {
        state.reloading = false;
        state.isLoading = false;
      })
      // SUBMIT ORDER
      .addCase(submitOrderAsync.pending, (state) => {
        state.isLoading = true;
        state.alertUser = null;
      })
      .addCase(submitOrderAsync.fulfilled, (state: any, action: any) => {
        state.order = undefined;
        state.isLoading = false;
        state.alertUser = { type: 'success', text: action.payload.message };
      })
      .addCase(submitOrderAsync.rejected, (state: any) => {
        state.isLoading = false;
      })
      // SUBMIT ORDER
      .addCase(saveOrderAsync.pending, (state) => {
        state.isLoading = true;
        state.alertUser = null;
      })
      .addCase(saveOrderAsync.fulfilled, (state: any, action: any) => {
        state.isLoading = false;
        state.order = action.payload.order;
        state.alertUser = { type: 'success', text: action.payload.message };
      })
      .addCase(saveOrderAsync.rejected, (state: any, action: any) => {
        state.isLoading = false;
        state.alertUser = { type: 'error', text: action.payload };
      })
      // CANCEL ORDER
      .addCase(cancelOrderAsync.pending, (state) => {
        state.isFetching = true;
        state.isFetchingText = 'Canceling order';
        state.alertUser = null;
      })
      .addCase(cancelOrderAsync.fulfilled, (state: any) => {
        state.isFetching = false;
        state.alertUser = { type: 'success', text: 'Order canceled.' };
      })
      .addCase(cancelOrderAsync.rejected, (state: any, action: any) => {
        state.isFetching = false;
        state.alertUser = { type: 'error', text: action.payload.text };
      })
      // UNLOCK PARTY (reset login)
      .addCase(unlockPartyAsync.pending, (state) => {
        state.isFetching = true;
        state.isFetchingText = 'Resetting login';
        state.alertUser = null;
      })
      .addCase(unlockPartyAsync.fulfilled, (state: any) => {
        state.isFetching = false;
        state.isFetchingText = null;
      })
      .addCase(unlockPartyAsync.rejected, (state: any) => {
        state.isFetching = false;
        state.isFetchingText = null;
      })
      // FETCH NOTARIES
      .addCase(fetchNotariesAsync.pending, (state) => {
        state.loadingRONNotaries = true;
        state.errors = undefined;
      })
      .addCase(fetchNotariesAsync.fulfilled, (state: any, action: any) => {
        state.loadingRONNotaries = false;
        state.notaries = action.payload;
        state.errors = undefined;
      })
      .addCase(fetchNotariesAsync.rejected, (state: any, action: any) => {
        state.loadingRONNotaries = false;
        state.notaries = null;
        state.errors = [action.payload];
      })
      // NOTARY REASSIGNED
      .addCase(changeNotaryAsync.pending, (state) => {
        state.isReassigningNotary = true;
        state.errors = undefined;
        state.isLoading = true;
      })
      .addCase(changeNotaryAsync.fulfilled, (state: any, action: any) => {
        state.isReassigningNotary = false;
        state.order = action.payload.order;
        state.errors = undefined;
        state.alertUser = { type: 'success', text: action.payload.message };
        state.isLoading = false;
      })
      .addCase(changeNotaryAsync.rejected, (state: any, action: any) => {
        state.isReassigningNotary = false;
        state.errors = [action.payload];
        state.isLoading = false;
      })
      // Add Co-Settlement Agents
      .addCase(addCoSettlementAgentsToOrderAsync.pending, (state) => {
        state.isAddingCoSettlement = true;
        state.errors = undefined;
        state.isLoading = true;
      })
      .addCase(
        addCoSettlementAgentsToOrderAsync.fulfilled,
        (state: any, action: any) => {
          state.isAddingCoSettlement = false;
          state.order = action.payload.order;
          state.errors = undefined;
          state.alertUser = { type: 'success', text: action.payload.message };
          state.isLoading = false;
        }
      )
      .addCase(
        addCoSettlementAgentsToOrderAsync.rejected,
        (state: any, action: any) => {
          state.isAddingCoSettlement = false;
          state.errors = [action.payload];
          state.isLoading = false;
        }
      )
      // UNLOCK SESSION (activate session)
      .addCase(unlockSessionAsync.pending, (state) => {
        state.isFetching = true;
        state.isFetchingText = 'Activating Session';
        state.alertUser = null;
      })
      .addCase(unlockSessionAsync.fulfilled, (state: any, action: any) => {
        state.isFetching = false;
        state.isFetchingText = null;
        state.errors = undefined;
        state.alertUser = { type: 'success', text: action.payload.message };
      })
      .addCase(unlockSessionAsync.rejected, (state: any, action: any) => {
        state.isFetching = false;
        state.isFetchingText = null;
        state.alertUser = { type: 'error', text: action.payload };
      })
      // UPLOAD INKSIGN DOCUMENT
      .addCase(UploadDocumentsOrderAsync.pending, (state) => {
        state.isFetching = true;
        state.alertUser = null;
      })
      .addCase(
        UploadDocumentsOrderAsync.rejected,
        (state: any, action: any) => {
          state.isFetching = false;
          state.alertUser = { type: 'error', text: action.payload };
        }
      )
      .addCase(UploadDocumentsOrderAsync.fulfilled, (state: any) => {
        state.isFetching = false;
        state.alertUser = {
          type: 'success',
          text: 'Your file was successfully uploaded',
        };
      })
      // UPDATE SIGNER INFORMATION
      .addCase(updateSignerInformationAsync.pending, (state) => {
        state.isIdentifingWitness = true;
        state.isLoading = true;
        state.alertUser = null;
      })
      .addCase(
        updateSignerInformationAsync.rejected,
        (state: any, action: any) => {
          state.isLoading = false;
          state.isIdentifingWitness = false;
          state.alertUser = { type: 'error', text: action.payload };
        }
      )
      .addCase(
        updateSignerInformationAsync.fulfilled,
        (state: any, action: any) => {
          state.isIdentifingWitness = false;
          state.isLoading = false;
          state.alertUser = { type: 'success', text: action.payload.message };
        }
      );
  },
});

export const {
  setAlert,
  updateCreateOrderObject,
  updateSelectedParty,
  updateSignerArray,
  updateEscrowTeamArray,
  updateSettlementTeamArray,
  uploadDocumentSuccess,
  uploadDocumentError,
  resetCreateFile,
  updateOrderDetailsSignerArray,
  setIsLoading,
} = ordersSlice.actions;

export const selectOrder = (state: RootState) => state.orders.order;

export default ordersSlice.reducer;
